import React, { useState, useCallback, useEffect, useRef } from "react";
import { useBuildingKey } from "../../Helpers/utils";
import COLORS from "../../Helpers/utils";
import { Helmet } from "react-helmet-async";
import { BreadcrumbComponent } from "../../Components/BreadCrumb";
import { BaseComponent, BuildingModel, PhotoModel } from "../../types/protocols";
import { translate } from "../../types/EnumTranslations";
import { BaseType } from "../../types/Enums";
import { useBuilding } from "../../Helpers/BuildingContext";
import { RELEVANT_BASE_TYPES } from "../../Helpers/baseTypeUtils";
import { useNavigate } from "react-router-dom";
import ClickableImage from "../../Components/ClickableImage";
import { BaseComponentImpl } from "../../types/implementations";
import ImageUploadModal from "./ImageUploadModal";
import { getDatabase, ref as dbRef, remove } from "firebase/database";

interface EditBuildingImagesProps {
    buildingData?: BuildingModel;
}

interface ComponentWithImages {
    component: BaseComponent;
    componentImpl: BaseComponentImpl;
    baseType: BaseType;
}

const EditBuildingImages: React.FC<EditBuildingImagesProps> = ({ buildingData: propBuilding }) => {
    const buildingKey = useBuildingKey();
    const navigate = useNavigate();
    const { currentBuilding, loadingBuilding, buildingError, fetchBuilding } = useBuilding();
    const [selectedComponent, setSelectedComponent] = useState<ComponentWithImages | null>(null);
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [droppedFiles, setDroppedFiles] = useState<File[]>([]);
    const [dragOverComponent, setDragOverComponent] = useState<string | null>(null);
    const [scrollPosition, setScrollPosition] = useState<number>(0);
    const [visibleComponentKey, setVisibleComponentKey] = useState<string | null>(null);
    const componentRefs = useRef<Map<string, HTMLDivElement>>(new Map());

    // Save scroll position to sessionStorage (session-only)
    const saveScrollPosition = useCallback(() => {
        const scrollY = window.scrollY;
        setScrollPosition(scrollY);
        sessionStorage.setItem(`editImages_scroll_${buildingKey}`, scrollY.toString());
        
        // Find the most visible component
        const viewportCenter = scrollY + window.innerHeight / 2;
        let closestComponent = '';
        let closestDistance = Infinity;
        
        componentRefs.current.forEach((element, componentKey) => {
            if (element) {
                const rect = element.getBoundingClientRect();
                const elementCenter = rect.top + scrollY + rect.height / 2;
                const distance = Math.abs(viewportCenter - elementCenter);
                
                if (distance < closestDistance) {
                    closestDistance = distance;
                    closestComponent = componentKey;
                }
            }
        });
        
        if (closestComponent) {
            setVisibleComponentKey(closestComponent);
            sessionStorage.setItem(`editImages_component_${buildingKey}`, closestComponent);
        }
    }, [buildingKey]);

    // Restore scroll position from sessionStorage
    const restoreScrollPosition = useCallback(() => {
        const savedScroll = sessionStorage.getItem(`editImages_scroll_${buildingKey}`);
        const savedComponent = sessionStorage.getItem(`editImages_component_${buildingKey}`);
        
        if (savedScroll) {
            setScrollPosition(parseInt(savedScroll));
        }
        if (savedComponent) {
            setVisibleComponentKey(savedComponent);
        }
    }, [buildingKey]);

    // Scroll to saved position or component
    const scrollToSavedPosition = useCallback(() => {
        if (visibleComponentKey && componentRefs.current.has(visibleComponentKey)) {
            const element = componentRefs.current.get(visibleComponentKey);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                return;
            }
        }
        
        if (scrollPosition > 0) {
            window.scrollTo({ top: scrollPosition, behavior: 'smooth' });
        }
    }, [scrollPosition, visibleComponentKey]);

    // Set up scroll position tracking
    useEffect(() => {
        if (!buildingKey) return;

        // Restore scroll position on component mount
        restoreScrollPosition();

        // Set up scroll event listener
        const handleScroll = () => {
            saveScrollPosition();
        };

        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
            // Clear session storage when component unmounts
            sessionStorage.removeItem(`editImages_scroll_${buildingKey}`);
            sessionStorage.removeItem(`editImages_component_${buildingKey}`);
        };
    }, [buildingKey, restoreScrollPosition, saveScrollPosition]);

    // Clear session storage when user navigates away
    useEffect(() => {
        const handleBeforeUnload = () => {
            if (buildingKey) {
                sessionStorage.removeItem(`editImages_scroll_${buildingKey}`);
                sessionStorage.removeItem(`editImages_component_${buildingKey}`);
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [buildingKey]);

    // Scroll to saved position after building data loads
    useEffect(() => {
        if (currentBuilding && (scrollPosition > 0 || visibleComponentKey)) {
            // Small delay to ensure DOM is fully rendered
            const timer = setTimeout(() => {
                scrollToSavedPosition();
            }, 100);
            
            return () => clearTimeout(timer);
        }
    }, [currentBuilding, scrollPosition, visibleComponentKey, scrollToSavedPosition]);

    const handleDragOver = useCallback((e: React.DragEvent, componentKey?: string) => {
        e.preventDefault();
        e.stopPropagation();
        if (componentKey) {
            setDragOverComponent(componentKey);
        }
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        // Only clear drag over if we're leaving the entire row
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setDragOverComponent(null);
        }
    }, []);

    const handleDrop = useCallback((e: React.DragEvent, componentWithImages: ComponentWithImages) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverComponent(null);
        
        const files = Array.from(e.dataTransfer.files).filter(file => 
            file.type.startsWith('image/')
        );
        
        if (files.length > 0) {
            setSelectedComponent(componentWithImages);
            setDroppedFiles(files);
            setUploadModalOpen(true);
        }
    }, []);

    const handleDeletePhoto = useCallback(async (photo: PhotoModel, componentKey: string) => {
        if (!buildingKey) {
            console.error('Building key is missing');
            return;
        }
        
        try {
            // Save current scroll position before refreshing
            saveScrollPosition();
            
            // Remove the photo from the database
            const database = getDatabase();
            const photoRef = dbRef(database, `buildings/${buildingKey}/structure/components/${componentKey}/photos/${photo.key}`);
            await remove(photoRef);
            
            // Refresh the building data to update the UI
            await fetchBuilding(buildingKey);
            
            console.log('Photo deleted successfully:', photo.key);
        } catch (error) {
            console.error('Error deleting photo:', error);
            alert('Ett fel uppstod vid borttagning av bilden. Försök igen.');
        }
    }, [buildingKey, fetchBuilding, saveScrollPosition]);

    // Early returns after all hooks
    if (loadingBuilding) return <p>Laddar byggnad</p>;
    if (buildingError) return <p style={{ color: "#ff0000" }}>{buildingError}</p>;
    if (!buildingKey) return <p>Byggnadsnyckel saknas</p>;
    if (!currentBuilding) return <p>Byggnad saknas</p>;

    const structure = currentBuilding.model.structure;
    if (!structure) {
        return <p>Structure missing</p>;
    }

    const filterComponentsByType = (components: { [key: string]: BaseComponent } = {}, type: BaseType) =>
        Object.fromEntries(
            Object.entries(components)
                .filter(([_, component]) => component.type === type)
        );

    const filterComponentsByRoomAndType = (components: { [key: string]: BaseComponent } = {}, type: BaseType, roomKey: string) =>
        Object.fromEntries(
            Object.entries(components)
                .filter(([_, component]) => component.type === type)
                .filter(([_, component]) => component.parentRoomKeys?.value?.some(key => key === roomKey))
        );

    // Get all components organized by base type
    const getComponentsByBaseType = (): { [key: string]: ComponentWithImages[] } => {
        const result: { [key: string]: ComponentWithImages[] } = {};

        // Add building-level components
        RELEVANT_BASE_TYPES.forEach(baseType => {
            const components = filterComponentsByType(structure.components, baseType);
            if (Object.keys(components).length > 0) {
                result[baseType] = Object.values(components).map(component => ({
                    component,
                    componentImpl: new BaseComponentImpl(component),
                    baseType
                }));
            }
        });

        // Add room-level components
        if (structure.rooms) {
            Object.values(structure.rooms).forEach(room => {
                [BaseType.Wall, BaseType.Ceiling, BaseType.Floor].forEach(baseType => {
                    const roomComponents = filterComponentsByRoomAndType(structure.components, baseType, room.key);
                    if (Object.keys(roomComponents).length > 0) {
                        const roomKey = `room_${room.key}_${baseType}`;
                        result[roomKey] = Object.values(roomComponents).map(component => ({
                            component,
                            componentImpl: new BaseComponentImpl(component),
                            baseType
                        }));
                    }
                });
            });
        }

        return result;
    };

    const componentsByBaseType = getComponentsByBaseType();

    const handleModalClose = () => {
        setUploadModalOpen(false);
        setSelectedComponent(null);
        setDroppedFiles([]);
    };

    const handleUploadSubmit = (photoModels: PhotoModel[]) => {
        console.log('Photos uploaded successfully:', photoModels);
        // Save current scroll position before refreshing
        saveScrollPosition();
        // Refresh building data to show new images
        if (buildingKey) {
            fetchBuilding(buildingKey);
        }
        handleModalClose();
    };

    return (
        <div style={{
            backgroundColor: "#f4f4f4",
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
        }}>
            <Helmet>
                <title>Hantera bilder: {currentBuilding?.summary.name}</title>
            </Helmet>

            <div style={{ flexShrink: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 20px' }}>
                    <BreadcrumbComponent
                        items={[
                            { text: currentBuilding?.summary.name ?? 'Byggnad', link: `/building/${buildingKey}` },
                            { text: "Hantera bilder" }
                        ]}
                    />
                    <button
                        onClick={() => navigate(`/building/${buildingKey}/allPhotos`)}
                        style={{
                            backgroundColor: '#6c757d',
                            color: 'white',
                            border: 'none',
                            padding: '10px 20px',
                            borderRadius: '5px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500'
                        }}
                    >
                        Tillbaka till bilder
                    </button>
                </div>
            </div>

            <div style={{
                flex: 1,
                padding: '20px',
                overflow: 'auto',
            }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                    {Object.entries(componentsByBaseType).map(([sectionKey, components]) => {
                        const isRoomSection = sectionKey.startsWith('room_');
                        const [roomKey, baseType] = isRoomSection ? sectionKey.split('_').slice(1) : [null, sectionKey];
                        const room = isRoomSection && structure.rooms && roomKey ? structure.rooms[roomKey] : null;
                        
                        return (
                            <div key={sectionKey} style={{ marginBottom: '40px' }}>
                                <h2 style={{ 
                                    marginBottom: '20px', 
                                    color: '#333'
                                }}>
                                    {isRoomSection && room 
                                        ? `${room.label || translate(room.type || 'room_general')} - ${translate(baseType as BaseType)}`
                                        : translate(baseType as BaseType)
                                    }
                                </h2>
                                
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '20px'
                                }}>
                                    {components.map((componentWithImages, index) => (
                                        <div 
                                            key={index} 
                                            ref={(el) => {
                                                if (el) {
                                                    componentRefs.current.set(componentWithImages.component.key, el);
                                                } else {
                                                    componentRefs.current.delete(componentWithImages.component.key);
                                                }
                                            }}
                                            onDragOver={(e) => handleDragOver(e, componentWithImages.component.key)}
                                            onDragLeave={handleDragLeave}
                                            onDrop={(e) => handleDrop(e, componentWithImages)}
                                            style={{
                                                backgroundColor: '#fff',
                                                borderRadius: '10px',
                                                padding: '20px',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                border: '1px solid #e0e0e0',
                                                display: 'flex',
                                                alignItems: 'stretch',
                                                gap: '20px',
                                                minHeight: '120px'
                                            }}>
                                            {/* Component Info */}
                                            <div style={{ flex: '1', minWidth: '200px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                                                <h4 style={{ margin: '0 0 10px 0', color: '#333', fontWeight: 'bold' }}>
                                                    <a
                                                        href={`/building/${buildingKey}/component/${componentWithImages.component.key}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{
                                                            color: COLORS.primaryDark,
                                                            textDecoration: 'none',
                                                            cursor: 'pointer',
                                                            fontSize: '14px',
                                                            fontWeight: 'bold'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.textDecoration = 'underline';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.textDecoration = 'none';
                                                        }}
                                                    >
                                                        {componentWithImages.componentImpl.displayName(currentBuilding)}
                                                        {componentWithImages.component.cardinalDirection?.value && 
                                                            ` (${translate(componentWithImages.component.cardinalDirection.value)})`
                                                        }
                                                    </a>
                                                </h4>
                                                <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>
                                                    {componentWithImages.component.label || 'Ingen beskrivning'}
                                                </p>
                                            </div>

                                            {/* Existing Images */}
                                            <div style={{ flex: '2', minWidth: '300px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                                {componentWithImages.component.photos && Object.values(componentWithImages.component.photos).length > 0 ? (
                                                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                                            {Object.values(componentWithImages.component.photos).map((photo) => (
                                                                       <ClickableImage
                                                                           key={photo.key}
                                                                           photo={photo}
                                                                           buildingKey={buildingKey}
                                                                           size={60}
                                                                           showDate={false}
                                                                           disableClick={false}
                                                                           showDeleteButton={true}
                                                                           onDeleteClick={(photo) => handleDeletePhoto(photo, componentWithImages.component.key)}
                                                                       />
                                                            ))}
                                                    </div>
                                                ) : (
                                                    <p style={{ margin: '0', color: '#999', fontSize: '14px' }}>Inga bilder</p>
                                                )}
                                            </div>

                                            {/* Drop Zone - Full Height */}
                                            <div
                                                style={{
                                                    flex: '1',
                                                    minWidth: '200px',
                                                    border: dragOverComponent === componentWithImages.component.key 
                                                        ? '2px dashed #007bff' 
                                                        : '2px dashed #ccc',
                                                    borderRadius: '8px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    backgroundColor: dragOverComponent === componentWithImages.component.key 
                                                        ? '#f0f8ff' 
                                                        : '#fafafa',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s ease',
                                                    alignSelf: 'stretch'
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (dragOverComponent !== componentWithImages.component.key) {
                                                        e.currentTarget.style.borderColor = '#007bff';
                                                        e.currentTarget.style.backgroundColor = '#f0f8ff';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (dragOverComponent !== componentWithImages.component.key) {
                                                        e.currentTarget.style.borderColor = '#ccc';
                                                        e.currentTarget.style.backgroundColor = '#fafafa';
                                                    }
                                                }}
                                            >
                                                <div style={{ textAlign: 'center', color: '#666' }}>
                                                    <div style={{ fontSize: '24px', marginBottom: '8px' }}>📁</div>
                                                    <div style={{ fontSize: '14px' }}>Dra och släpp bilder här</div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {uploadModalOpen && selectedComponent && (
                <ImageUploadModal
                    isOpen={uploadModalOpen}
                    onClose={handleModalClose}
                    onSubmit={handleUploadSubmit}
                    files={droppedFiles}
                    component={selectedComponent.component}
                    building={currentBuilding}
                    buildingKey={buildingKey}
                />
            )}
        </div>
    );
};

export default EditBuildingImages;
